// src/components/PositionTracker.tsx

import React, { useState, useEffect } from "react";
import { getPositions, checkExits, closePosition, deletePositions, getPendingActions, triggerPositionAnalysis } from "../api/etfApi";
import type { Position, ExitSignal } from "../api/myTypes";

const PositionTracker: React.FC = () => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [exitSignals, setExitSignals] = useState<ExitSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingExits, setCheckingExits] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [triggeringAnalysis, setTriggeringAnalysis] = useState(false);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);

  useEffect(() => {
    loadPositions();
    // Set up periodic polling every 15 minutes (900000 ms)
    const interval = setInterval(() => {
      checkPendingActions();
    }, 900000); // 15 minutes
    
    // Check immediately on mount
    checkPendingActions();
    
    // Listen for refresh events from Track Position button
    const handleRefresh = () => {
      loadPositions();
      checkPendingActions();
    };
    window.addEventListener('refresh-positions', handleRefresh);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('refresh-positions', handleRefresh);
    };
  }, []);

  useEffect(() => {
    // Reload positions when pending actions change
    if (pendingActionsCount > 0) {
      loadPositions();
    }
  }, [pendingActionsCount]);

  useEffect(() => {
    // Clear selected position if it no longer exists (closed or deleted)
    if (selectedPosition) {
      const positionExists = positions.some(p => p.position_id === selectedPosition && p.status === "open");
      if (!positionExists) {
        setSelectedPosition(null);
        setAnalysisResult(null);
      }
    }
  }, [positions, selectedPosition]);

  const checkPendingActions = async () => {
    try {
      const data = await getPendingActions();
      setPendingActionsCount(data.count);
      // Update positions with pending action status
      if (data.count > 0) {
        loadPositions();
      }
    } catch (err: any) {
      console.error("Error checking pending actions:", err);
    }
  };

  const loadPositions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPositions();
      setPositions(data.positions);
      
      // Check for pending actions
      const pendingData = await getPendingActions();
      setPendingActionsCount(pendingData.count);
    } catch (err: any) {
      setError(err.message || "Failed to load positions");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckExits = async () => {
    setCheckingExits(true);
    setError(null);
    try {
      const data = await checkExits();
      setExitSignals(data.exit_signals);
      // Reload positions so list shows updated pending_action (e.g. "ACTION REQUIRED" when take profit/stop loss hit)
      await loadPositions();
    } catch (err: any) {
      setError(err.message || "Failed to check exit conditions");
    } finally {
      setCheckingExits(false);
    }
  };

  const handleClosePosition = async (positionId: string, exitPrice: number) => {
    try {
      await closePosition({ positionId, exitPrice });
      
      // Clear selected position and analysis if this position was selected
      if (selectedPosition === positionId) {
        setSelectedPosition(null);
        setAnalysisResult(null);
      }
      
      await loadPositions();
      setExitSignals(exitSignals.filter(s => s.position_id !== positionId));
      window.dispatchEvent(new CustomEvent("refresh-performance"));
      alert("Position closed successfully! The result is saved and will appear in the Performance tab.");
    } catch (err: any) {
      alert(`Error closing position: ${err.message}`);
    }
  };

  const handleToggleSelection = (positionId: string) => {
    setSelectedForDeletion(prev => {
      const newSet = new Set(prev);
      if (newSet.has(positionId)) {
        newSet.delete(positionId);
      } else {
        newSet.add(positionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedForDeletion.size === positions.length) {
      setSelectedForDeletion(new Set());
    } else {
      setSelectedForDeletion(new Set(positions.map(p => p.position_id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedForDeletion.size === 0) return;
    
    if (!confirm(`Are you sure you want to delete ${selectedForDeletion.size} position(s)? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const idsToDelete = Array.from(selectedForDeletion);
      
      // Clear selected position and analysis if any deleted position was selected
      if (selectedPosition && selectedForDeletion.has(selectedPosition)) {
        setSelectedPosition(null);
        setAnalysisResult(null);
      }
      
      await deletePositions(idsToDelete);
      
      // Remove deleted positions from the list
      setPositions(prev => prev.filter(p => !selectedForDeletion.has(p.position_id)));
      
      // Clear selection
      setSelectedForDeletion(new Set());
      
      // Reload positions to ensure consistency
      await loadPositions();
    } catch (err: any) {
      console.error("Error deleting positions:", err);
      alert(`Error deleting positions: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleSelectPosition = (positionId: string) => {
    setSelectedPosition(positionId);
    const position = positions.find(p => p.position_id === positionId);
    if (position?.last_analysis_result) {
      setAnalysisResult(position.last_analysis_result);
    } else {
      setAnalysisResult(null);
    }
  };

  const handleTriggerAnalysis = async (positionId: string) => {
    setTriggeringAnalysis(true);
    try {
      const result = await triggerPositionAnalysis(positionId);
      setAnalysisResult(result.analysis);
      await loadPositions(); // Reload to get updated position data
    } catch (err: any) {
      alert(`Error triggering analysis: ${err.message}`);
    } finally {
      setTriggeringAnalysis(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ padding: "2rem", textAlign: "center" }}>
        <p>Loading positions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: "2rem" }}>
        <div style={{ color: "#ef4444", marginBottom: "1rem" }}>Error: {error}</div>
        <button className="primary-button" onClick={loadPositions}>Retry</button>
      </div>
    );
  }

  // If no open positions, show empty state
  if (positions.length === 0) {
    return (
      <div className="card" style={{ padding: "2rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142", textAlign: "center" }}>
        <p style={{ color: "#ffffff" }}>No open positions tracked.</p>
        <p style={{ color: "#9ca3af", fontSize: "0.875rem", marginTop: "0.5rem" }}>
          Use "Track Position" button on recommendations to start tracking.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* Header with Check Exits Button */}
      <div className="card" style={{ padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ color: "#ffffff" }}>
            Position Tracker
            {pendingActionsCount > 0 && (
              <span style={{ 
                fontSize: "0.875rem", 
                marginLeft: "0.5rem", 
                fontWeight: "normal",
                backgroundColor: "#ef4444",
                color: "white",
                padding: "0.25rem 0.5rem",
                borderRadius: "4px"
              }}>
                {pendingActionsCount} Pending Action{pendingActionsCount !== 1 ? 's' : ''}
              </span>
            )}
          </h3>
          <button
            className="primary-button"
            onClick={handleCheckExits}
            disabled={checkingExits || positions.length === 0}
            style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
          >
            {checkingExits ? "Checking..." : "Check Exit Conditions"}
          </button>
        </div>
      </div>

      {/* Exit Signals */}
      {exitSignals.length > 0 && (
        <div className="card" style={{ padding: "1.5rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", marginBottom: "1rem" }}>Exit Signals</h3>
          {exitSignals.map((signal) => {
            const exit = signal.exit_signal;
            const urgencyColor = exit.urgency === "High" ? "#ef4444" : exit.urgency === "Moderate" ? "#f59e0b" : "#10b981";
            return (
              <div
                key={signal.position_id}
                style={{
                  padding: "1rem",
                  marginBottom: "0.75rem",
                  backgroundColor: "#0f1117",
                  borderRadius: "4px",
                  border: `2px solid ${urgencyColor}`
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                  <div>
                    <span style={{ color: "#ffffff", fontWeight: "bold" }}>{signal.ticker}</span>
                    <span
                      style={{
                        marginLeft: "0.5rem",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "4px",
                        backgroundColor: urgencyColor,
                        color: "white",
                        fontSize: "0.75rem",
                        fontWeight: "bold"
                      }}
                    >
                      {exit.exit_signal}
                    </span>
                  </div>
                  <span style={{ color: urgencyColor, fontWeight: "bold" }}>{exit.urgency} Urgency</span>
                </div>
                <div style={{ color: "#ffffff", marginBottom: "0.5rem" }}>{exit.exit_reason}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Entry: </span>
                    <span style={{ color: "#ffffff" }}>${exit.entry_price.toFixed(2)}</span>
                  </div>
                  <div>
                    <span style={{ color: "#9ca3af" }}>Current: </span>
                    <span style={{ color: "#ffffff" }}>${exit.current_price.toFixed(2)}</span>
                  </div>
                  <div>
                    <span style={{ color: "#9ca3af" }}>P&L: </span>
                    <span style={{ color: exit.unrealized_pnl_pct >= 0 ? "#10b981" : "#ef4444" }}>
                      {exit.unrealized_pnl_pct >= 0 ? "+" : ""}{exit.unrealized_pnl_pct.toFixed(2)}%
                    </span>
                  </div>
                </div>
                {exit.exit_reasoning && (
                  <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.5rem", fontStyle: "italic" }}>
                    {exit.exit_reasoning}
                  </div>
                )}
                {exit.exit_signal !== "HOLD" && exit.recommended_exit_price && (
                  <button
                    className="primary-button"
                    onClick={() => handleClosePosition(signal.position_id, exit.recommended_exit_price!)}
                    style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
                  >
                    Close Position @ ${exit.recommended_exit_price.toFixed(2)}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Analysis Results Panel */}
      {selectedPosition && (
        <div className="card" style={{ padding: "1.5rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 style={{ color: "#ffffff" }}>Analysis Results</h3>
            <button
              className="primary-button"
              onClick={() => handleTriggerAnalysis(selectedPosition)}
              disabled={triggeringAnalysis}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              {triggeringAnalysis ? "Analyzing..." : "Update Analysis"}
            </button>
          </div>
          {analysisResult ? (
            <div style={{ color: "#ffffff" }}>
              <div style={{ marginBottom: "1rem" }}>
                <span style={{ 
                  padding: "0.5rem 1rem", 
                  borderRadius: "4px",
                  backgroundColor: analysisResult.action === "SELL" ? "#ef4444" : "#10b981",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1rem"
                }}>
                  {analysisResult.action}
                </span>
              </div>
              {analysisResult.reasoning && (
                <div style={{ marginBottom: "1rem", color: "#9ca3af" }}>
                  <strong style={{ color: "#ffffff" }}>Reasoning:</strong>
                  <p style={{ marginTop: "0.5rem" }}>{analysisResult.reasoning}</p>
                </div>
              )}
              {analysisResult.recommendation && (
                <div style={{ marginBottom: "1rem" }}>
                  <strong style={{ color: "#ffffff" }}>Recommendation:</strong>
                  <pre style={{ 
                    marginTop: "0.5rem", 
                    padding: "1rem", 
                    backgroundColor: "#0f1117", 
                    borderRadius: "4px",
                    color: "#9ca3af",
                    fontSize: "0.875rem",
                    overflow: "auto"
                  }}>
                    {JSON.stringify(analysisResult.recommendation, null, 2)}
                  </pre>
                </div>
              )}
              {analysisResult.analysis_timestamp && (
                <div style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                  Analysis Time: {new Date(analysisResult.analysis_timestamp).toLocaleString()}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: "#9ca3af" }}>
              No analysis available. Click "Update Analysis" to trigger AI analysis for this position.
            </div>
          )}
        </div>
      )}

      {/* Open Positions */}
      <div className="card" style={{ padding: "1.5rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3 style={{ color: "#ffffff" }}>
              Open Positions ({positions.length})
              {selectedForDeletion.size > 0 && (
                <span style={{ color: "#ef4444", fontSize: "0.875rem", marginLeft: "0.5rem", fontWeight: "normal" }}>
                  ({selectedForDeletion.size} selected)
                </span>
              )}
            </h3>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              {selectedForDeletion.size > 0 && (
                <button
                  className="primary-button"
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                  style={{ 
                    fontSize: "0.875rem", 
                    padding: "0.5rem 1rem",
                    backgroundColor: "#ef4444",
                    borderColor: "#ef4444"
                  }}
                >
                  {deleting ? "Deleting..." : `Delete Selected (${selectedForDeletion.size})`}
                </button>
              )}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {/* Select All Checkbox */}
            {positions.length > 0 && (
              <div style={{ padding: "0.5rem", backgroundColor: "#0f1117", borderRadius: "4px", border: "1px solid #2c3142" }}>
                <label style={{ color: "#9ca3af", fontSize: "0.875rem", display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={selectedForDeletion.size === positions.length && positions.length > 0}
                    onChange={handleSelectAll}
                    style={{ cursor: "pointer" }}
                  />
                  Select All ({positions.length})
                </label>
              </div>
            )}
            {positions.map((position) => {
              const isSelectedForDeletion = selectedForDeletion.has(position.position_id);
              const isActionable = !!position.pending_action;
              const isSelected = selectedPosition === position.position_id;
              
              return (
                <div
                  key={position.position_id}
                  onClick={() => handleSelectPosition(position.position_id)}
                  style={{
                    padding: "1rem",
                    backgroundColor: isSelected ? "#1a2a3a" : isSelectedForDeletion ? "#2a1a1a" : isActionable ? "#2a1a1a" : "#0f1117",
                    borderRadius: "4px",
                    border: isSelected ? "2px solid #3b82f6" : isSelectedForDeletion ? "2px solid #ef4444" : isActionable ? "2px solid #ef4444" : "1px solid #2c3142",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <label 
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelectedForDeletion}
                          onChange={() => handleToggleSelection(position.position_id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: "pointer", marginRight: "0.5rem" }}
                        />
                      </label>
                      <span style={{ color: "#ffffff", fontWeight: "bold", fontSize: "1.1rem" }}>{position.ticker}</span>
                      {isActionable && (
                        <span style={{
                          padding: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          backgroundColor: "#ef4444",
                          color: "white",
                          fontSize: "0.75rem",
                          fontWeight: "bold"
                        }}>
                          ACTION REQUIRED
                        </span>
                      )}
                      {position.action && (
                        <span style={{
                          padding: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          backgroundColor: position.action === "SELL" ? "#ef4444" : "#10b981",
                          color: "white",
                          fontSize: "0.75rem",
                          fontWeight: "bold"
                        }}>
                          {position.action}
                        </span>
                      )}
                    </div>
                    <span style={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                      Opened: {new Date(position.entry_timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem", fontSize: "0.875rem" }}>
                    <div>
                      <span style={{ color: "#9ca3af" }}>Entry: </span>
                      <span style={{ color: "#ffffff" }}>${position.entry_price.toFixed(2)}</span>
                    </div>
                    {position.current_price && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Current: </span>
                        <span style={{ color: "#ffffff" }}>${position.current_price.toFixed(2)}</span>
                      </div>
                    )}
                    {position.stop_loss && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Stop Loss: </span>
                        <span style={{ color: "#ef4444" }}>${position.stop_loss.toFixed(2)}</span>
                      </div>
                    )}
                    {position.take_profit && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Take Profit: </span>
                        <span style={{ color: "#10b981" }}>${position.take_profit.toFixed(2)}</span>
                      </div>
                    )}
                    {position.position_size && (
                      <div>
                        <span style={{ color: "#9ca3af" }}>Size: </span>
                        <span style={{ color: "#ffffff" }}>{position.position_size}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
    </div>
  );
};

export default PositionTracker;
